import { useState, useMemo, useEffect, useCallback, useRef } from "react"
import useReportingDataLoading from "../DataLoading/useReportingDataLoading"
import { convertDataToTable } from "utils/convertDataToTable"
import { flattenCodebookForSelect } from "utils/codebookUtils"
import { ScriptGenerationService } from "services/ScriptGenerationService"
import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import { TabSettings } from "types/ReportingDashboard/reporting.types"
import {
  DTAB_STATISTIC_OPTIONS,
  XTAB_STATISTIC_OPTIONS,
} from "features/Reporting/components/ReportingDisplay/Elements/shared/StatisticTypeSelectOptions"

// Types
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { OperatorOption, SelectOption } from "types/ui.types"
import { EngineOutputItem } from "types/engineOutput.types"
import { RTableContent } from "types/engineOutput.types"
import { TransformedTableData } from "utils/convertDataToTable"

/**
 * Parameters for useReportTableContent hook
 */
export interface UseReportTableContentParams {
  /** Data codebook for variable information */
  codebook: CodebookData
  /** Query instance for data operations */
  queryInstance: QueryInstance | null
  /** Current filter configuration */
  filter: string
  /** Unique tab key identifier */
  tabKey: string
  /** Auto-run script content */
  autoRunScriptContent: string
  /** Initial settings from configuration */
  initialSettings: TabSettings
  /** Callback when settings change */
  onSettingsChange: (settings: TabSettings) => void
  /** Function to request sequential execution */
  onRequestExecution: (tabKey: string, reason: string) => void
}

/**
 * Return type for useReportTableContent hook
 */
export interface UseReportTableContentReturn {
  // State
  currentTableData: EngineOutputItem | null
  rowVariables: string[]
  columnVariables: string[]
  selectedOperators: STATISTIC_TYPE[]
  tableOperationType: TABLE_OPERATION_TYPE
  removedElements: { columns: Set<string>; rows: Set<string> }
  persistentRows: Set<string>
  isRunningScript: boolean

  // Computed values
  selectOptions: SelectOption[]
  operatorOptions: OperatorOption[]
  transformedTableData: TransformedTableData | null

  // Actions
  setRowVariables: (variables: string[]) => void
  setColumnVariables: (variables: string[]) => void
  setSelectedOperators: (operators: STATISTIC_TYPE[]) => void
  setPersistentRows: (rows: Set<string>) => void
  handleTableOperationTypeChange: (newType: TABLE_OPERATION_TYPE) => void
  handleRun: () => void
  runScript: (options?: {
    selectedColumns?: Set<string>
    selectedRows?: Set<string>
  }) => Promise<boolean>
  triggerRunScript: () => Promise<boolean>
  getTableData: (skipRun?: boolean) => Promise<(string | number | null)[][]>
  hasData: () => boolean
}

/**
 * Custom hook that encapsulates all the business logic for ReportTableContent
 * Handles script generation, data loading, table configuration, and state management
 */
export const useReportTableContent = ({
  codebook,
  queryInstance,
  filter,
  tabKey,
  autoRunScriptContent,
  initialSettings,
  onSettingsChange,
  onRequestExecution,
}: UseReportTableContentParams): UseReportTableContentReturn => {
  // ========================================================================================
  // HOOKS AND STATE
  // ========================================================================================

  const { executeScript, isLoading } = useReportingDataLoading(
    queryInstance,
    false // Not time series - expects single result
  )

  const [currentTableData, setCurrentTableData] =
    useState<EngineOutputItem | null>(null)
  const [rowVariables, setRowVariables] = useState<string[]>(
    initialSettings.rowVariables || []
  )
  const [columnVariables, setColumnVariables] = useState<string[]>(
    initialSettings.columnVariables || []
  )
  const [selectedOperators, setSelectedOperators] = useState<STATISTIC_TYPE[]>(
    initialSettings.selectedOperators || [
      STATISTIC_TYPE.N,
      STATISTIC_TYPE.BIG_N,
      STATISTIC_TYPE.ROW_PERCENT,
      STATISTIC_TYPE.COL_PERCENT,
      STATISTIC_TYPE.INDEX,
      STATISTIC_TYPE.PERC_PERCENT,
    ]
  )
  const [tableOperationType, setTableOperationType] =
    useState<TABLE_OPERATION_TYPE>(
      initialSettings.tableOperationType || TABLE_OPERATION_TYPE.XTAB
    )

  const [removedElements, setRemovedElements] = useState(() => ({
    columns: new Set(initialSettings.removedElements?.columns || []),
    rows: new Set(initialSettings.removedElements?.rows || []),
  }))

  const [persistentRows, setPersistentRows] = useState<Set<string>>(
    new Set(initialSettings.persistentRows || [])
  )

  // Add state to track if table has been manually run or loaded from config
  const [hasBeenRun, setHasBeenRun] = useState<boolean>(false)
  const [isLoadingFromConfig, setIsLoadingFromConfig] = useState<boolean>(false)
  const [autoRunAttempts, setAutoRunAttempts] = useState<number>(0) // Simple retry limit

  const configLoadedRef = useRef<boolean>(false)

  const isRunningScript = isLoading

  // ========================================================================================
  // MEMOIZED VALUES
  // ========================================================================================

  // Flatten codebook for select options
  const selectOptions = useMemo(() => {
    if (!codebook) return []
    return flattenCodebookForSelect(codebook)
  }, [codebook])

  // Get operator options based on table operation type
  const operatorOptions: OperatorOption[] = useMemo(() => {
    const options =
      tableOperationType === TABLE_OPERATION_TYPE.DTAB
        ? DTAB_STATISTIC_OPTIONS
        : XTAB_STATISTIC_OPTIONS

    return options.map((opt) => ({
      value: opt.value,
      label: opt.label,
    }))
  }, [tableOperationType])

  // Convert table data for display
  const transformedTableData = useMemo(() => {
    if (!currentTableData) return null

    // Type guard to ensure we have RTableContent
    const isRTableContent = (
      item: EngineOutputItem
    ): item is EngineOutputItem & { content: RTableContent } => {
      return (
        item.content &&
        typeof item.content === "object" &&
        "data" in item.content &&
        Array.isArray((item.content as RTableContent).data)
      )
    }

    if (!isRTableContent(currentTableData)) {
      return null
    }

    try {
      return convertDataToTable(currentTableData.content)
    } catch {
      return null
    }
  }, [currentTableData])

  // Create a ref to always have access to the latest transformedTableData
  const transformedTableDataRef = useRef(transformedTableData)

  // Update ref whenever transformedTableData changes
  useEffect(() => {
    transformedTableDataRef.current = transformedTableData
  }, [transformedTableData])

  // ========================================================================================
  // EFFECTS
  // ========================================================================================

  /**
   * Effect to handle initial settings changes and configuration loading
   */
  useEffect(() => {
    const prevRenderRowVariables = rowVariables
    const prevRenderColumnVariables = columnVariables

    // Check if this is loading from saved config (has variables)
    const isConfigLoad =
      initialSettings.rowVariables?.length > 0 &&
      initialSettings.columnVariables?.length > 0

    setRowVariables(initialSettings.rowVariables || [])
    setColumnVariables(initialSettings.columnVariables || [])

    const convertedOperators = (initialSettings.selectedOperators || []).map(
      (op) => {
        // If it's already a STATISTIC_TYPE, use it; if it's a string from JSON, convert it
        return typeof op === "string" ? (op as STATISTIC_TYPE) : op
      }
    )
    setSelectedOperators(convertedOperators)

    // Ensure tableOperationType has a valid value with fallback
    const validOperationType =
      initialSettings.tableOperationType || TABLE_OPERATION_TYPE.XTAB
    setTableOperationType(validOperationType)

    // Initialize removed elements from config
    setRemovedElements({
      columns: new Set(initialSettings.removedElements?.columns || []),
      rows: new Set(initialSettings.removedElements?.rows || []),
    })
    setPersistentRows(new Set(initialSettings.persistentRows || []))

    // Set loading from config flag if we have variables in initial settings
    if (isConfigLoad && !configLoadedRef.current) {
      setIsLoadingFromConfig(true)
      setHasBeenRun(true) // Mark as having been run since it came from config
      setAutoRunAttempts(0) // Reset attempts for new config
      configLoadedRef.current = true
    }

    if (
      JSON.stringify(initialSettings.rowVariables || []) !==
        JSON.stringify(prevRenderRowVariables) ||
      JSON.stringify(initialSettings.columnVariables || []) !==
        JSON.stringify(prevRenderColumnVariables)
    ) {
      setCurrentTableData(null)
      // Only reset removed elements if not coming from config
      if (!initialSettings.removedElements && !initialSettings.persistentRows) {
        setRemovedElements({ columns: new Set(), rows: new Set() })
        setPersistentRows(new Set())
      }
    }
  }, [initialSettings, tabKey])

  /**
   * Auto-run effect - ORIGINAL WORKING LOGIC
   */
  useEffect(() => {
    // Don't auto-run if too many attempts to prevent infinite loops
    if (autoRunAttempts >= 2) {
      return
    }

    const hasValidVariables =
      rowVariables.length > 0 && columnVariables.length > 0
    const needsData =
      hasValidVariables && !currentTableData && !isLoading && queryInstance

    // Only auto-run if:
    // 1. Loading from config (table was previously configured)
    // 2. OR table has been run before but needs fresh data (like after filter change)
    const shouldAutoRun =
      needsData && (isLoadingFromConfig || (hasBeenRun && !currentTableData))

    if (shouldAutoRun && onRequestExecution) {
      const reason = isLoadingFromConfig
        ? "loading from config"
        : "refreshing existing table"

      // Clear the loading from config flag after first auto-run request
      if (isLoadingFromConfig) {
        setIsLoadingFromConfig(false)
      }

      // Increment attempt counter
      setAutoRunAttempts((prev) => prev + 1)

      // Request execution through parent's sequential system
      onRequestExecution(tabKey, reason)
    }
  }, [
    rowVariables,
    columnVariables,
    queryInstance,
    currentTableData,
    isLoading,
    isLoadingFromConfig,
    hasBeenRun,
    tabKey,
    autoRunAttempts,
    onRequestExecution,
  ])

  // ========================================================================================
  // SCRIPT GENERATION AND EXECUTION
  // ========================================================================================

  /**
   * Generates the complex script with QTABLE definitions and removal commands
   */
  const generateScript = useCallback(
    ({
      selectedColumns = new Set(),
      selectedRows = new Set(),
    }: {
      selectedColumns?: Set<string>
      selectedRows?: Set<string>
    } = {}): string => {
      // Combine existing removed elements with any new selections
      const combinedRemovedColumns = new Set([
        ...removedElements.columns,
        ...selectedColumns,
      ])
      const combinedRemovedRows = new Set([...persistentRows, ...selectedRows])

      // Use the centralized script generation service
      return ScriptGenerationService.generateComplexScript({
        tableId: tabKey,
        rowVariables,
        columnVariables,
        selectedOperators,
        tableOperationType,
        filter,
        autoRunScriptContent,
        removedColumns: combinedRemovedColumns,
        removedRows: combinedRemovedRows,
      })
    },
    [
      tabKey,
      selectedOperators,
      autoRunScriptContent,
      rowVariables,
      columnVariables,
      tableOperationType,
      filter,
      removedElements,
      persistentRows,
    ]
  )

  /**
   * Executes script using the standardized hook (ORIGINAL LOGIC)
   */
  const runScript = useCallback(
    async ({
      selectedColumns = new Set(),
      selectedRows = new Set(),
    }: {
      selectedColumns?: Set<string>
      selectedRows?: Set<string>
    } = {}): Promise<boolean> => {
      if (isLoading) return Promise.resolve(false)

      if (!queryInstance) {
        console.error("Cannot run script: Query instance not available.")
        return Promise.resolve(false)
      }

      if (rowVariables.length === 0 || columnVariables.length === 0) {
        const isUserInitiatedSettingsRun =
          selectedColumns.size === 0 && selectedRows.size === 0
        if (isUserInitiatedSettingsRun) {
          console.error(
            "Please select at least one row and one column variable."
          )
          return Promise.resolve(false)
        }
      }

      // Mark as having been run (for future filter-based auto-runs)
      setHasBeenRun(true)

      try {
        const script = generateScript({ selectedColumns, selectedRows })

        const result = await executeScript(script, tabKey)

        if (result.success) {
          // Ensure we have a single EngineOutputItem, not an array
          const dataItem = Array.isArray(result.data)
            ? result.data.length > 0
              ? result.data[0]
              : null
            : result.data
          setCurrentTableData(dataItem || null)

          // Reset auto-run attempts on success
          setAutoRunAttempts(0)

          // Update removal state for interaction runs
          const isSettingsRun =
            selectedColumns.size === 0 && selectedRows.size === 0

          let newRemovedElements: { columns: Set<string>; rows: Set<string> }
          let newPersistentRows: Set<string>

          if (isSettingsRun) {
            newRemovedElements = { columns: new Set(), rows: new Set() }
            newPersistentRows = new Set()
            setRemovedElements(newRemovedElements)
            setPersistentRows(newPersistentRows)
          } else {
            const columnsToEffectivelyRemoveThisRun = new Set([
              ...removedElements.columns,
              ...selectedColumns,
            ])
            const allRowsEverMarkedForRemoval = new Set([
              ...persistentRows,
              ...selectedRows,
            ])

            newRemovedElements = {
              columns: columnsToEffectivelyRemoveThisRun,
              rows: allRowsEverMarkedForRemoval,
            }
            newPersistentRows = allRowsEverMarkedForRemoval

            setPersistentRows(newPersistentRows)
            setRemovedElements(newRemovedElements)
          }

          // Always save settings including removed elements
          if (onSettingsChange) {
            onSettingsChange({
              rowVariables,
              columnVariables,
              selectedOperators,
              tableOperationType,
              removedElements: {
                columns: Array.from(newRemovedElements.columns),
                rows: Array.from(newRemovedElements.rows),
              },
              persistentRows: Array.from(newPersistentRows),
            })
          }

          return true
        } else {
          console.error(`Script execution failed for ${tabKey}:`, result.error)
          return false
        }
      } catch (error) {
        console.error(`Script generation error for ${tabKey}:`, error)
        return false
      }
    },
    [
      isLoading,
      queryInstance,
      rowVariables,
      columnVariables,
      selectedOperators,
      tableOperationType,
      generateScript,
      executeScript,
      tabKey,
      onSettingsChange,
      removedElements,
      persistentRows,
    ]
  )

  const triggerRunScript = useCallback(async (): Promise<boolean> => {
    if (rowVariables.length > 0 && columnVariables.length > 0 && !isLoading) {
      return runScript()
    }
    return Promise.resolve(false)
  }, [runScript, rowVariables, columnVariables, isLoading])

  const handleRun = useCallback((): void => {
    onRequestExecution(tabKey, "manual_run")
  }, [onRequestExecution, tabKey])

  // ========================================================================================
  // TABLE DATA EXPORT
  // ========================================================================================

  const getTableData = useCallback(
    async (skipRun: boolean = false): Promise<(string | number | null)[][]> => {
      // If we need to run the script and don't have data, do that first
      if (!skipRun && !transformedTableDataRef.current) {
        const success = await triggerRunScript()
        if (!success) {
          return [["Failed to generate data"]]
        }

        // Wait for React state updates with retries
        let retries = 0
        const maxRetries = 10
        while (retries < maxRetries && !transformedTableDataRef.current) {
          await new Promise((resolve) => setTimeout(resolve, 100))
          retries++
        }
      }

      // Use the current ref value which should always have the latest data
      const currentTransformedData = transformedTableDataRef.current

      // Check again for transformed data after potential script execution
      if (!currentTransformedData) {
        return [["No data available"]]
      }

      // Combine headers and rows for complete Excel export
      const result: (string | number | null)[][] = []

      // Add headers
      if (
        currentTransformedData.headers &&
        currentTransformedData.headers.length > 0
      ) {
        // Process headers to create flat header rows
        if (currentTransformedData.hasSubHeaders) {
          // Create main header row
          const mainHeaderRow: (string | number | null)[] = []
          const subHeaderRow: (string | number | null)[] = []

          currentTransformedData.headers.forEach((headerGroup) => {
            if (
              "subHeaders" in headerGroup &&
              headerGroup.subHeaders.length > 0
            ) {
              // Add main header with colspan equivalent (repeat for each sub-header)
              headerGroup.subHeaders.forEach((subHeader, index) => {
                mainHeaderRow.push(index === 0 ? headerGroup.header : null)
                subHeaderRow.push(subHeader)
              })
            } else {
              mainHeaderRow.push(headerGroup.header)
              subHeaderRow.push(null)
            }
          })

          result.push(mainHeaderRow)
          result.push(subHeaderRow)
        } else {
          // Simple header row - ensure we properly extract headers
          const headerRow = currentTransformedData.headers.map((h) => h.header)
          result.push(headerRow)
        }
      }

      // Add data rows
      if (
        currentTransformedData.rows &&
        currentTransformedData.rows.length > 0
      ) {
        result.push(...currentTransformedData.rows)
      }

      return result
    },
    [triggerRunScript]
  )

  const hasData = useCallback(
    () => Boolean(transformedTableData),
    [transformedTableData]
  )

  // ========================================================================================
  // EVENT HANDLERS
  // ========================================================================================

  const handleTableOperationTypeChange = useCallback(
    (newType: TABLE_OPERATION_TYPE) => {
      setTableOperationType(newType)

      // Update operators based on operation type
      const newOperatorOptions =
        newType === TABLE_OPERATION_TYPE.DTAB
          ? DTAB_STATISTIC_OPTIONS
          : XTAB_STATISTIC_OPTIONS

      let newSelectedOperators = selectedOperators.filter((op) =>
        newOperatorOptions.some((option) => option.value === op)
      )

      if (newSelectedOperators.length === 0) {
        // If no operators are valid, use defaults
        const defaultOperators = newOperatorOptions
          .slice(0, 6)
          .map((op) => op.value)
        newSelectedOperators = defaultOperators
      }

      setSelectedOperators(newSelectedOperators)

      // Sync settings to parent immediately
      const newSettings: TabSettings = {
        rowVariables,
        columnVariables,
        selectedOperators: newSelectedOperators,
        tableOperationType: newType,
        removedElements: {
          columns: Array.from(removedElements.columns),
          rows: Array.from(removedElements.rows),
        },
        persistentRows: Array.from(persistentRows),
      }
      onSettingsChange(newSettings)
    },
    [
      selectedOperators,
      rowVariables,
      columnVariables,
      removedElements,
      persistentRows,
      onSettingsChange,
    ]
  )

  // ========================================================================================
  // RETURN HOOK VALUES
  // ========================================================================================

  return {
    // State
    currentTableData,
    rowVariables,
    columnVariables,
    selectedOperators,
    tableOperationType,
    removedElements,
    persistentRows,
    isRunningScript,

    // Computed values
    selectOptions,
    operatorOptions,
    transformedTableData,

    // Actions
    setRowVariables,
    setColumnVariables,
    setSelectedOperators,
    setPersistentRows,
    handleTableOperationTypeChange,
    handleRun,
    runScript,
    triggerRunScript,
    getTableData,
    hasData,
  }
}

export default useReportTableContent
